"use client"

import * as React from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { SORT_OPTIONS } from "@/constant/constant"

interface PlanFiltersProps {
  lifeCover: string
  onLifeCoverChange: (value: string) => void
  policyTerm: string
  onPolicyTermChange: (value: string) => void
  paymentFrequency: "annually" | "monthly"
  onPaymentFrequencyChange: (value: "annually" | "monthly") => void
  planType: "all" | "individual" | "sme"
  onPlanTypeChange: (value: "all" | "individual" | "sme") => void
  sortBy: string
  onSortChange: (value: string) => void
  onClearFilters?: () => void
  activeFilterCount?: number
}

function FilterDropdown({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  className?: string
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const selectedLabel =
    options.find((o) => o.value === value)?.label || options[0]?.label

  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <label className="mb-0.5 block text-xs text-slate-500 dark:text-slate-400">
        {label}
      </label>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-sm font-semibold text-slate-900 hover:text-nova-600 dark:text-white dark:hover:text-slate-300"
      >
        {selectedLabel}
        <ChevronDown className="h-4 w-4 text-slate-400 dark:text-slate-500" />
      </button>

      {isOpen && (
        <div className="absolute top-full z-50 mt-1 min-w-[180px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value)
                setIsOpen(false)
              }}
              className={cn(
                "w-full px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 dark:hover:bg-slate-800",
                value === option.value
                  ? "bg-nova-50 text-nova-700 dark:bg-slate-800 dark:text-white"
                  : "text-slate-700 dark:text-slate-300"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function PlanFilters({ sortBy, onSortChange }: PlanFiltersProps) {
  return (
    <FilterDropdown
      label="Sort"
      value={sortBy}
      options={SORT_OPTIONS}
      onChange={onSortChange}
    />
  )
}
